/** 逐单归因的纯逻辑（无 React）。
 *
 * 规格 `docs/design/screens/attribution.md`：主表以**信号下场**为行（受理的每条信号恰好一行，
 * `GET /dispositions`），不再以成交为行——以成交为行看不到「受理未下单」的信号。
 * 链路分区：信号 → 研究流程与参数 → 数据快照 → 风控 → 订单 → 成交 → 仓位 → 版本，
 * 取不到的环显式标「缺」，不用别的字段顶上。
 */

import type { G2Decision, G2Disposition, G2Order, G2Position, G2Trade, RunTrace } from "../api/g2";

export const ORDERED = "已下单";

/** 页头计数：受理 N（运行归档 `signals.accepted`）与有下场 M（`/dispositions`）。 */
export interface HeadCount {
  accepted: number | null;
  dispositions: number;
  /** 两数都有且对不上：留痕不完整（内核自检本应拦下）。 */
  mismatch: boolean;
}

export function headCount(accepted: string[] | null, dispositions: G2Disposition[]): HeadCount {
  const n = accepted === null ? null : accepted.length;
  return { accepted: n, dispositions: dispositions.length, mismatch: n !== null && n !== dispositions.length };
}

/** 下场行的「决策留痕」状态：已下单却没有决策留痕是红色缺口；未下单不算缺口。 */
export function decisionGap(row: G2Disposition, decisions: Record<string, G2Decision>): string | null {
  if (row.disposition !== ORDERED) return null;
  if (!row.order_id) return "已下单却没有订单号（缺）";
  return decisions[row.order_id] ? null : "有订单无决策留痕（缺）";
}

/** 链路的一环：`value` 为 `null` 即缺；`muted` 表示「未下单，这一环本来就没有」。 */
export interface ChainLink {
  label: string;
  value: string | null;
  why: string;
  muted?: boolean;
}

/** 由一条下场行与同一本账的订单 / 成交 / 仓位 / 决策拼出链路。 */
export function buildChain(
  row: G2Disposition,
  ctx: {
    decisions: Record<string, G2Decision>;
    orders: G2Order[];
    trades: G2Trade[];
    positions: G2Position[];
  },
): ChainLink[] {
  const ordered = row.disposition === ORDERED && row.order_id;
  const decision = ordered ? ctx.decisions[row.order_id as string] : undefined;
  const noOrder = `无订单：${row.disposition}${row.detail ? `（${row.detail}）` : ""}`;
  const fromDecision = (label: string, pick: (d: G2Decision) => string): ChainLink => {
    if (!ordered) return { label, value: noOrder, why: "", muted: true };
    if (!decision) return { label, value: null, why: "有订单无决策留痕" };
    const value = pick(decision);
    return { label, value: value || null, why: `decisions.${label} 为空` };
  };
  const links: ChainLink[] = [
    { label: "信号", value: `${row.signal_id} · ${row.instrument_id}`, why: "" },
    fromDecision("研究流程", (d) => d.research_flow),
    fromDecision("参数集", (d) => d.params_digest),
    fromDecision("数据快照", (d) => d.data_snapshot),
  ];
  if (!ordered) {
    links.push({ label: "风控校验", value: row.disposition === "风控拒单" ? `风控拒单：${row.detail ?? "原因见风险报告"}` : noOrder, why: "", muted: true });
    for (const label of ["订单", "成交", "仓位"]) links.push({ label, value: noOrder, why: "", muted: true });
    return links;
  }
  links.push({
    label: "风控校验",
    value: decision?.risk_adjustment ? `数量调整：${JSON.stringify(decision.risk_adjustment)}` : null,
    why: "未记录逐层校验（risk_checks 待加；本单无数量调整）",
  });
  const order = ctx.orders.find((item) => item.client_order_id === row.order_id);
  links.push({
    label: "订单",
    value: order ? `${order.client_order_id} · ${order.status}${order.reason ? `（${order.reason}）` : ""} · 券商单号 ${order.venue_order_id ?? "未回报"}` : null,
    why: "本账 orders 里找不到该订单",
  });
  const trades = ctx.trades.filter((trade) => trade.client_order_id === row.order_id);
  links.push({
    label: "成交",
    value: trades.length ? trades.map((trade) => `${trade.trade_id} ${trade.last_qty}@${trade.slipped_px}`).join("；") : null,
    why: order?.status === "REJECTED" ? "被拒，无成交" : "无成交（去对账屏核对）",
  });
  const positionIds = [...new Set(trades.map((trade) => trade.position_id).filter((id): id is string => Boolean(id)))];
  const positions = ctx.positions.filter((position) => positionIds.includes(position.position_id));
  links.push({
    label: "仓位",
    value: positions.length ? positions.map((p) => `${p.position_id}${p.closed ? `（已平，均价 ${p.avg_px_close ?? "—"}）` : "（持有中）"}`).join("；") : null,
    why: trades.length ? "成交没有挂到仓位" : "无成交即无仓位",
  });
  return links;
}

/** 版本环：策略代码 / 依赖 / 配置 / 模型四项。旧运行没有 `versions` 时整组缺。 */
export function versionLinks(run: RunTrace | null): ChainLink[] {
  const versions = run?.versions ?? null;
  const pick = (key: string): string | null => {
    const value = versions?.[key];
    return typeof value === "string" && value ? value : null;
  };
  const whyOld = run ? "该运行早于版本留痕（runs/{run_id}.versions 待加）" : "这本账未绑定运行";
  const code = pick("code_commit");
  return [
    { label: "策略代码", value: code ? `${code}${versions?.["code_dirty"] === true ? "（运行时代码未提交）" : ""}` : null, why: versions ? "取不到 git HEAD" : whyOld },
    { label: "依赖", value: pick("deps_digest"), why: versions ? "取不到 uv.lock 摘要" : whyOld },
    { label: "配置", value: pick("config_digest") ?? run?.config_digest ?? null, why: whyOld },
    { label: "模型", value: pick("model_version"), why: "信号合约无模型版本字段" },
  ];
}

/** 仓位首笔信号：成交 → 订单 → 决策三跳；任一跳取不到返回 `null`（屏显示「缺」）。 */
export function firstSignalOf(positionId: string, trades: G2Trade[], decisions: Record<string, G2Decision>): string | null {
  const first = trades
    .filter((trade) => trade.position_id === positionId)
    .sort((a, b) => a.ts_event - b.ts_event)[0];
  if (!first) return null;
  return decisions[first.client_order_id]?.signal_id ?? null;
}
